import { AssistantRuntimeProvider } from "@assistant-ui/react";
import { useChatRuntime, AssistantChatTransport } from "@assistant-ui/react-ai-sdk";
import { ReactNode, useMemo, createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

interface JudgmentReference {
  id: string;
  målnummer: string;
  domstol: string;
  instans: string;
  dom_datum: string;
  parter: string;
}

export interface SearchFiltersState {
  category: string;
  instances: string[];
  courts: string[];
  yearFrom: number;
  yearTo: number;
  caseNumbers: string[];
}

interface ChatContextType {
  referencedJudgments: JudgmentReference[];
  setReferencedJudgments: (judgments: JudgmentReference[]) => void;
  filters: SearchFiltersState;
  setFilters: (filters: SearchFiltersState) => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within ChatProvider");
  }
  return context;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [referencedJudgments, setReferencedJudgments] = useState<JudgmentReference[]>([]);
  const [filters, setFilters] = useState<SearchFiltersState>({
    category: "Entreprenadrätt",
    instances: [],
    courts: [],
    yearFrom: 1999,
    yearTo: new Date().getFullYear(),
    caseNumbers: [],
  });
  const filtersRef = useRef(filters);

  useEffect(() => {
    console.log("[ChatProvider] Filters updated", filters);
    filtersRef.current = filters;
  }, [filters]);

  // Use production API when deployed, localhost for development
  const apiUrl = window.location.hostname === 'localhost'
    ? "http://localhost:8000/api/v1/chat/agent"
    : `${import.meta.env.VITE_API_URL || "https://api.forsete.brickanta.com"}/api/v1/chat/agent`;
  const token = localStorage.getItem("access_token") || "";

  console.log("ChatProvider - API URL:", apiUrl);
  console.log("ChatProvider - Token:", token ? "***" + token.slice(-10) : "NO TOKEN");

  // Listen for referenced-judgments events from SSE stream
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'referenced-judgments' && data.judgments) {
          console.log("Captured referenced-judgments event:", data.judgments);
          setReferencedJudgments(data.judgments);
        }
      } catch (e) {
        // Not JSON or not our event, ignore
      }
    };

    // Note: We can't easily intercept AssistantChatTransport's EventSource,
    // so instead we'll use a custom fetch wrapper
    return () => {};
  }, []);

  const transport = useMemo(() => {
    const originalFetch = window.fetch;

    async function buildRequestWithFilters(
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<RequestInfo | URL | Request> {
      let baseRequest: Request;

      if (input instanceof Request) {
        baseRequest = input.clone();
      } else {
        baseRequest = new Request(input, init);
      }

      let bodyText: string | null = null;
      try {
        bodyText = await baseRequest.text();
      } catch (error) {
        console.warn("Unable to read request body for filter injection", error);
      }

      if (!bodyText) {
        return baseRequest;
      }

      try {
        const parsed = JSON.parse(bodyText);
        parsed.filters = filtersRef.current;

        const headers = new Headers(baseRequest.headers);
        headers.set("Content-Type", "application/json");

        const requestWithFilters = new Request(baseRequest, {
          body: JSON.stringify(parsed),
          headers,
        });

        console.log("[ChatProvider] Injected filters into request body", {
          filters: filtersRef.current,
          originalUrl: baseRequest.url,
          method: baseRequest.method,
        });
        console.debug("[ChatProvider] Request payload with filters", parsed);
        return requestWithFilters;
      } catch (error) {
        console.error("Failed to inject filters into request body", error);
        return baseRequest;
      }
    }

    const customTransport = new AssistantChatTransport({
      api: apiUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      fetch: async (input, init) => {
        const requestWithFilters = await buildRequestWithFilters(input, init);

        const response = await originalFetch(requestWithFilters);
        console.log("[ChatProvider] Sent request with filters", {
          url: requestWithFilters instanceof Request ? requestWithFilters.url : String(requestWithFilters),
          appliedFilters: filtersRef.current,
        });

        // Clone the response so we can read the stream twice
        const clonedResponse = response.clone();

        // Start reading the stream in the background to intercept custom events
        (async () => {
          const reader = clonedResponse.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) return;

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n');

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') continue;

                  try {
                    const parsed = JSON.parse(data);
                    if (parsed.type === 'referenced-judgments' && parsed.judgments) {
                      console.log("Captured referenced-judgments event:", parsed.judgments);
                      setReferencedJudgments(parsed.judgments);
                    }
                  } catch (e) {
                    // Ignore parse errors
                  }
                }
              }
            }
          } finally {
            reader.releaseLock();
          }
        })();

        return response;
      },
    });

    return customTransport;
  }, [apiUrl, token, filters]);

  const runtime = useChatRuntime({
    transport,
  });

  console.log("ChatProvider - Runtime created with config:", {
    api: apiUrl,
    hasAuthHeader: !!token
  });

  return (
    <ChatContext.Provider value={{ referencedJudgments, setReferencedJudgments, filters, setFilters }}>
      <AssistantRuntimeProvider runtime={runtime}>
        {children}
      </AssistantRuntimeProvider>
    </ChatContext.Provider>
  );
}
